import React, { useState, useCallback, useEffect } from 'react';
import { 
  Upload, 
  X, 
  Image, 
  CheckCircle,
  AlertCircle,
  FileImage
} from 'lucide-react';

const ImageUpload = ({ onUploadSuccess, showNotification }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadLog, setUploadLog] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadActive, setUploadActive] = useState(false);

  // No polling - status will be updated when APIs return values

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      showNotification('Only image files are allowed', 'warning');
    }
    
    setSelectedFiles(prev => [...prev, ...imageFiles]);
  }, [showNotification]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      showNotification('Please select files to upload', 'error');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('https://outiloeuvresvolees.inouiagency.com/api/upload-image', {
          method: 'POST',
          body: formData
        });

        const result = await response.json();

        if (result.success) {
          successCount++;
        } else {
          errorCount++;
          console.error(`Failed to upload ${file.name}:`, result.error);
        }
      } catch (error) {
        errorCount++;
        console.error(`Error uploading ${file.name}:`, error);
      }
    }

    setUploading(false);
    setSelectedFiles([]);
    // Start showing the upload compare status area
    setUploadActive(true);

    if (successCount > 0) {
      showNotification(
        `Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}`,
        'success'
      );
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    }

    if (errorCount > 0) {
      showNotification(
        `Failed to upload ${errorCount} image${errorCount > 1 ? 's' : ''}`,
        'error'
      );
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Upload className="h-5 w-5 text-purple-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800">Upload Stolen Images</h2>
      </div>

      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="space-y-4">
          <div className="flex justify-center">
            <FileImage className="h-12 w-12 text-gray-400" />
          </div>
          <div>
            <p className="text-lg font-medium text-gray-900">
              Drop your stolen images here
            </p>
            <p className="text-sm text-gray-600">
              or click to browse your files
            </p>
          </div>
          <div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
            >
              <Upload className="h-4 w-4" />
              <span>Choose Files</span>
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Supported formats: JPG, PNG, GIF, BMP, TIFF, WebP (Max 16MB each)
          </p>
        </div>
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Selected Files ({selectedFiles.length})
          </h3>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <Image className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Button */}
      {selectedFiles.length > 0 && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={uploadFiles}
            disabled={uploading}
            className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                <span>Upload {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Tips for better results:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Use high-resolution images when possible</li>
              <li>Include multiple angles of the same item if available</li>
              <li>Clear, well-lit photos work best for matching</li>
              <li>Remove any watermarks or text overlays if possible</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Live Upload Compare Status */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Compare Status</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-700">Progress</span>
            <span className="text-sm font-medium text-gray-900">{uploadProgress.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div className={`h-2 rounded-full ${uploadActive ? 'bg-purple-600' : 'bg-gray-400'}`} style={{ width: `${uploadProgress}%` }}></div>
          </div>
          <div className="h-40 overflow-auto bg-black rounded p-3 text-xs font-mono">
            {uploadLog.length === 0 ? (
              <div className="text-gray-400 text-center py-6">
                <p>Upload comparison log will appear here...</p>
                <p className="text-[10px]">This log is separate from the Search log.</p>
              </div>
            ) : (
              <div className="space-y-1">
                {uploadLog.map((line, idx) => {
                  let color = 'text-gray-300';
                  if (line.includes('MATCH')) color = 'text-yellow-300';
                  if (line.includes('ERROR')) color = 'text-red-300';
                  if (line.includes('STOP')) color = 'text-orange-300';
                  if (line.includes('INFO')) color = 'text-purple-300';
                  return (
                    <div key={idx} className={`${color} whitespace-pre-wrap`}>{line}</div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;

